export const ripFormSchema = {
  api: {
    submit: 'rips',
  },
  payload: {
    includeRawFormData: true,
    casts: {
      formVersionNumber: 'int',
      clinx002Value: 'number',
      cumulativeAmountBilled: 'number',
      currentCLINX002Balance: 'number',
      newCLINX002Balance: 'number',
    },
    // Transform estimates from form data to API structure
    transform: (data) => {
      const transformed = { ...data };
      const ripNumber = data.ripNumber || 'RIP-UNKNOWN';
      
      // Build estimates array from form data
      if (Array.isArray(data.estimates) && data.estimates.length > 0) {
        transformed.estimates = [];
        
        data.estimates.forEach((estimate, index) => {
          // Filter out blank line items (no description and zero cost)
          const validLineItems = (estimate.lineItems || []).filter(item => {
            const hasDescription = item.description && item.description.trim().length > 0;
            const hasCost = (item.totalLineItemEstimate || 0) > 0;
            return hasDescription || hasCost;
          });
          
          if (validLineItems.length > 0) {
            const estimateNumber = String(index + 1).padStart(3, '0');
            transformed.estimates.push({
              estimateType: 'RIPEstimate',
              objectName: `${ripNumber}-${estimateNumber}`,
              lineItems: validLineItems,
              totalEstimate: estimate.totalEstimate || validLineItems.reduce((sum, item) => sum + (item.totalLineItemEstimate || 0), 0),
            });
          }
        });
        
        // If no valid estimates, remove the field
        if (transformed.estimates.length === 0) {
          delete transformed.estimates;
        }
      }
      
      return transformed;
    },
    // Optional: allow-list to control payload shape strictly
    allow: [
      'associatedLineOfAccounting',
      'awardPIID',
      'clientName',
      'clinx002Value',
      'contractorName',
      'cumulativeAmountBilled',
      'currentCLINX002Balance',
      'estimates',
      'formId',
      'formVersionNumber',
      'fromContactId',
      'newCLINX002Balance',
      'projectId',
      'projectName',
      'purposeJustification',
      'rawFormData',
      'ripDate',
      'ripNumber',
      'subject',
      'throughContactId',
      'toContactId',
      'description',
      'dueDate',
    ],
  },
  blocks: [
    // Hidden read-only values fetched from forms API (not rendered)
    {
      type: 'hiddenApi',
      route: 'forms',
      // Find the RIP form record by formType
      filter: { formType: 'Request to Initiate Purchase (RIP) Form' },
      map: {
        formId: 'formId',
        formVersionNumber: 'versionNumber',
      },
    },
    { type: 'heading', label: 'Request to Initiate Purchase (RIP)' },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'ripNumber', label: 'RIP Number', placeholder: 'RIP-2025-001', required: true, flex: '1 1 300px' },
        { type: 'date', name: 'dueDate', label: 'Due Date', required: false, payloadTransform: (v) => (v && v !== '') ? v : undefined, flex: '0 0 200px' },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'description', label: 'Description', required: false, multiline: true, minRows: 3 },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'date', name: 'ripDate', label: 'RIP Date', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'projectId',
          label: 'Project Name',
          required: true,
          placeholder: 'Search project',
          loadFromApiRoute: 'projects',
          loadFromApiField: 'projects',
          optionLabelKey: 'title',
          optionValueKey: 'projectId',
          mapFields: { projectName: 'title' },
          helperText: 'Select a project (stores ID, displays title).'
        },
        { type: 'text', name: 'awardPIID', label: 'Award PIID Number', required: true, placeholder: 'Award PIID Number' }
      ]
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'contractorName', label: 'Contractor Name', placeholder: 'MindPoint Group LLC', required: true },
        { type: 'text', name: 'clientName', label: 'Client Name', placeholder: 'Department of Defense', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'associatedLineOfAccounting', label: 'Associated Line of Accounting', placeholder: 'LOA-2025-001', required: true },
        { type: 'text', name: 'clinx002Value', label: 'CLIN X002 Value', placeholder: '5000', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'toContactId',
          label: 'TO Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { toContactName: 'name' }
        },
        {
          type: 'autocomplete',
          name: 'fromContactId',
          label: 'FROM Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { fromContactName: 'name' }
        }
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'throughContactId',
          label: 'THROUGH Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { throughContactName: 'name' }
        },
        { type: 'text', name: 'cumulativeAmountBilled', label: 'Cumulative Amount Billed', required: true, placeholder: '15000' },
      ],
    },
    { type: 'group', fields: [ { type: 'text', name: 'currentCLINX002Balance', label: 'Current CLIN X002 Balance', required: true, placeholder: '10000' } ] },
    { type: 'group', fields: [ { type: 'text', name: 'newCLINX002Balance', label: 'New CLIN X002 Balance', required: true, placeholder: '5000' } ] },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'subject', label: 'Subject', placeholder: 'Reimbursement request for project expenses', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'purposeJustification',
          label: 'Purpose/Justification of Request',
          required: true,
          multiline: true,
          placeholder: '',
        },
      ],
    },
    // Estimated Purchase Cost Section
    {
      type: 'group',
      fields: [
        {
          type: 'estimateTable',
          name: 'estimates',
          label: 'ESTIMATED PURCHASE COST:',
          required: true,
          estimateType: 'RIPEstimate',
          indirectCostLabel: 'Contractor',
        },
      ],
    },
  ],
};
